/** Small signal fragments drift at different depths behind the product. */
export function createSignalParticles(canvas: HTMLCanvasElement, getProgress: () => number, reduced: boolean) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  let seed = 2026;
  const random = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  const stars = Array.from({ length: 200 }, () => ({ x: random(), y: random(), depth: .3 + random(), radius: .3 + random() * .9, phase: random() * Math.PI * 2, color: random() > .6 ? '161,237,255' : '207,184,255' }));
  let width = 1, height = 1, ratio = 1, frame = 0, last = 0, elapsed = 0;
  let visible = false;
  let progress = getProgress();
  const pointer = { x: 0, y: 0 };
  const camera = { x: 0, y: 0 };

  function draw(now: number) {
    frame = 0;
    if (!reduced && now - last < 32) { frame = requestAnimationFrame(draw); return; }
    if (last && !reduced) elapsed += Math.min(now - last, 64) / 1000;
    last = now;
    progress += ((reduced ? 0 : getProgress()) - progress) * .08;
    camera.x += (pointer.x - camera.x) * .04;
    camera.y += (pointer.y - camera.y) * .04;
    ctx!.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx!.clearRect(0, 0, width, height);
    for (const star of stars) {
      const x = (star.x * width + camera.x * 14 * star.depth + Math.sin(elapsed * .025 + star.phase) * 4 + progress * (star.x - .5) * 90 + width) % width;
      const y = (star.y * height + camera.y * 9 * star.depth - elapsed * star.depth * .35 - progress * 65 * star.depth + height * 10) % height;
      const alpha = .22 + (reduced ? .08 : (Math.sin(elapsed * .55 + star.phase) + 1) * .14);
      ctx!.fillStyle = `rgba(${star.color},${alpha})`;
      const size = star.radius * star.depth;
      ctx!.fillRect(x, y, size * 2.4, Math.max(.5, size * .65));
    }
    if (!reduced && visible && !document.hidden) frame = requestAnimationFrame(draw);
  }
  function schedule() {
    if (!frame && visible && !document.hidden) { last = 0; frame = requestAnimationFrame(draw); }
  }
  function resize() {
    const bounds = canvas.getBoundingClientRect();
    width = Math.max(1, bounds.width); height = Math.max(1, bounds.height);
    ratio = Math.min(window.devicePixelRatio || 1, 1.5);
    canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio);
    schedule();
  }
  function move(event: PointerEvent) {
    if (event.pointerType === 'mouse') { pointer.x = event.clientX / window.innerWidth - .5; pointer.y = event.clientY / window.innerHeight - .5; }
  }
  function resetPointer() { pointer.x = pointer.y = 0; }
  function visibility() {
    if (document.hidden) { cancelAnimationFrame(frame); frame = 0; } else schedule();
  }
  const intersection = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    if (visible) schedule(); else { cancelAnimationFrame(frame); frame = 0; }
  });
  const observer = new ResizeObserver(resize);
  observer.observe(canvas); intersection.observe(canvas);
  document.addEventListener('visibilitychange', visibility);
  if (!reduced) {
    window.addEventListener('pointermove', move, { passive: true });
    document.documentElement.addEventListener('pointerleave', resetPointer);
  }
  resize();
  return () => {
    cancelAnimationFrame(frame); observer.disconnect(); intersection.disconnect();
    document.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('pointermove', move);
    document.documentElement.removeEventListener('pointerleave', resetPointer);
  };
}
